import { type ScrollViewValue } from "@jsidea/blocks/controls";
import { type TimelineItem } from "@jsidea/blocks/supports";
import { Signal } from "../core/Signal";
import { Calc } from "../math/Calc";
import { wp } from "./Plugins";
import { ViewTimeline } from "../display/ViewTimeline";

export interface IPlayer<T extends IPlayer<T> = IPlayer<any>> {
    isReady: boolean;
    onReady: Signal<[T]>;
    onEnterFrame: Signal<[T]>;
    get progress(): number;
    set progress(value: number);
}

export interface PlayerControllerOptions {
    once: boolean;
    loop: boolean;
    player: IPlayer<any>;
    element: HTMLElement;
    timeline: TimelineItem;
    scrollView: ScrollViewValue;
    start: number;
    end: number;
    duration: number;
}

export class PlayerController implements IPlayer {
    public isReady: boolean = true;
    public onReady = Signal.create<[IPlayer]>();
    public onEnterFrame = Signal.create<[IPlayer]>();

    private _player: IPlayer;
    private _timeline: TimelineItem;
    private _element: HTMLElement;
    private _scrollView: ScrollViewValue;
    private _once: boolean;
    private _loop: boolean;
    private _progress: number = 0;
    private _disposables: DisposableObject[] = [];
    private _start: number;
    private _end: number;
    private _duration: number;

    constructor(options: PlayerControllerOptions) {
        const { player, timeline, element, scrollView, once } = options;

        this._once = once;
        this._player = player;
        this._timeline = timeline || { kind: "viewport-in", name: "" };
        this._element = element;
        this._scrollView = scrollView;
        this._start = options.start === undefined ? 0 : options.start;
        this._end = options.end === undefined ? 1 : options.end;
        this._duration = options.duration || 1;
        this._loop = options.loop || false;

        if (timeline?.kind === "scroll" || timeline?.kind === "scroll-self") {
            this.setScrollTimeline();
        } else if (timeline?.kind === "viewport-in") {
            this.setViewportIn();
        } else if (
            timeline?.kind === "image-sequence" ||
            timeline?.kind === "lottie"
        ) {
            this.setSyncPlayer();
        } else {
            console.warn(`Unsupported timeline kind "${timeline?.kind}".`);
            return;
        }
    }

    public get progress(): number {
        return this._progress;
    }

    public set progress(value: number) {
        this.setProgress(value);
    }

    private setProgress(progress: number): void {
        this._progress = Calc.clamp(progress, 0, 1);
        if (this._player.isReady) {
            if (this._start === 0 && this._end === 1) {
                this._player.progress = progress;
            } else {
                const range = this._end - this._start;
                const value = (progress - this._start) / range;
                const p = Calc.clamp(value, 0, 1);
                this._player.progress = p;
            }
        }
        this.onEnterFrame.dispatch(this);
    }

    private setSyncPlayer() {
        const player = this._player;
        const timelineRef = this._timeline;

        this._disposables.push(
            wp.playerSync.registerConsumer({
                guid: timelineRef.guid,
                kind: timelineRef.kind,
                onInit: (sync) => {
                    if (sync.isReady) {
                        this.setProgress(sync.progress);
                        return;
                    }

                    this._disposables.push(
                        sync.onReady.once(() => {
                            this.setProgress(sync.progress);
                        })
                    );
                },
                onProgress: (sync, progress) => {
                    this.setProgress(progress);
                },
                onDispose: () => {
                    this.dispose();
                },
            })
        );
    }

    private setViewportIn() {
        const element = this._element;
        const player = this._player;

        const once = this._once;
        const loop = this._loop;
        const duration = this._duration;

        const tween = gsap.to(this, {
            duration: duration,
            ease: "none",
            onUpdate: () => {
                this.setProgress(tween.progress());
            },
            onComplete: () => {
                if (once && !loop) {
                    this.setProgress(1);
                    this.dispose();
                    return;
                }

                if (loop) {
                    tween.restart();
                } else {
                    tween.pause();
                }
            },
        });
        tween.pause();

        this._disposables.push({
            dispose: () => {
                tween.kill();
            },
        });

        let visible = false;
        const updatePlayState = () => {
            if (!player.isReady) {
                return;
            }

            if (visible) {
                if (loop) {
                    tween.play();
                } else if (once) {
                    tween.play();
                } else if (!once) {
                    tween.play(0, true);
                }
            } else {
                if (loop) {
                    tween.pause();
                } else if (once) {
                    this.setProgress(1);
                    this.dispose();
                } else if (!once) {
                    this.setProgress(0);
                    tween.pause(0, true);
                }
            }
        };

        this._disposables.push(
            player.onReady.add(() => {
                if (!visible) {
                    return;
                }
                updatePlayState();
            })
        );

        const observer = new IntersectionObserver((entries) => {
            if (visible == entries[0].isIntersecting) {
                return;
            }
            visible = entries[0].isIntersecting;
            updatePlayState();
        });

        observer.observe(element);

        this._disposables.push({
            dispose: () => {
                observer.disconnect();
            },
        });
    }

    private setScrollTimeline() {
        const element = this._element;
        const player = this._player;
        const timelineRef = this._timeline;
        const scrollView = this._scrollView;

        let target: HTMLElement;
        if (timelineRef.kind === "scroll-self") {
            target = element;
        } else if (timelineRef.kind === "scroll") {
            target = element.ownerDocument.querySelector(
                `.jsidea-scroll-timeline--${timelineRef.guid}`
            );
        }

        if (!target) {
            console.warn(
                `No target found for play controller "${timelineRef?.guid}".`
            );
            return;
        }

        const scrollViewportValue = scrollView;
        const timeline = new ViewTimeline({
            target,
            rangeStart: scrollViewportValue?.rangeStart,
            rangeEnd: scrollViewportValue?.rangeEnd,
            onUpdate: (timeline) => {
                this.setProgress(timeline.ratio);
            },
        });
        this._disposables.push(
            player.onReady.add(() => {
                player.progress = timeline.ratio;
            })
        );
        this._disposables.push(timeline);
    }

    public dispose() {
        if (!this._disposables) {
            return;
        }
        this._disposables.forEach((d) => d.dispose());
        this._disposables = null;
        this._element = null;
        this._player = null;
        this._scrollView = null;
        this._timeline = null;
    }
}
